import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { UnavailabilityPeriod } from "@/types/org";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const toClock = (value?: string): string => {
  if (!value) return "";
  if (!value.includes("T")) return value.slice(0, 5);
  const timePart = value.split("T")[1] ?? "";
  const timeOnly = timePart.split(/[+Z-]/)[0] || "";
  return timeOnly.slice(0, 5);
};

export function orgCalendarToday(timeZone: string): string {
  return dayjs().tz(timeZone).format("YYYY-MM-DD");
}

const calendarDay = (iso: string): string => iso.split("T")[0] ?? "";

export const getHolidayFormClock = (period: {
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
}): { startTime: string; endTime: string; allDay: boolean } => {
  const dateStart = toClock(period.startDate) || "00:00";
  const dateEnd = toClock(period.endDate) || "00:00";
  const hasSeparateTimes = Boolean(period.startTime && period.endTime);
  const useSeparateTimes = hasSeparateTimes && dateStart === "00:00";
  const startTime = useSeparateTimes ? toClock(period.startTime) : dateStart;
  const endTime = useSeparateTimes ? toClock(period.endTime) : dateEnd || "23:59";
  const allDay =
    !useSeparateTimes &&
    startTime === "00:00" &&
    (endTime === "23:59" || endTime === "00:00");

  return { startTime, endTime, allDay };
};

export const isAllDayHoliday = (
  period: Parameters<typeof getHolidayFormClock>[0]
): boolean => getHolidayFormClock(period).allDay;

export const resolveHolidayInstantRange = (
  period: UnavailabilityPeriod,
  timeZone: string
): { start: dayjs.Dayjs; end: dayjs.Dayjs } | null => {
  const startDay = calendarDay(period.startDate);
  const endDay = calendarDay(period.endDate);
  if (!startDay || !endDay) return null;

  if (period.startTime && period.endTime) {
    const start = dayjs.tz(
      `${startDay} ${toClock(period.startTime)}`,
      "YYYY-MM-DD HH:mm",
      timeZone
    );
    const endClock = toClock(period.endTime);
    const end =
      endClock === "23:59"
        ? dayjs.tz(endDay, timeZone).add(1, "day").startOf("day")
        : dayjs.tz(`${endDay} ${endClock}`, "YYYY-MM-DD HH:mm", timeZone);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;
    return { start, end };
  }

  const start = dayjs(period.startDate);
  const end = dayjs(period.endDate);
  if (!start.isValid() || !end.isValid()) return null;

  const timePart = period.startDate.split("T")[1] ?? "";
  const hasOffset = /(?:Z|[+-]\d{2}:\d{2})$/.test(timePart);
  const startClock = toClock(period.startDate);
  const endClock = toClock(period.endDate);

  const zonedStart = hasOffset
    ? start.tz(timeZone)
    : dayjs.tz(`${startDay} ${startClock || "00:00"}`, "YYYY-MM-DD HH:mm", timeZone);
  let zonedEnd = hasOffset
    ? end.tz(timeZone)
    : dayjs.tz(`${endDay} ${endClock || "00:00"}`, "YYYY-MM-DD HH:mm", timeZone);

  const expandToNextMidnight =
    endClock === "23:59" ||
    (startClock === "00:00" && endClock === "00:00" && !zonedEnd.isAfter(zonedStart));
  if (expandToNextMidnight) {
    zonedEnd = dayjs.tz(endDay, timeZone).add(1, "day").startOf("day");
  }

  if (!zonedEnd.isAfter(zonedStart)) return null;
  return { start: zonedStart, end: zonedEnd };
};

const isExclusiveMidnight = (end: dayjs.Dayjs, start: dayjs.Dayjs): boolean =>
  end.hour() === 0 &&
  end.minute() === 0 &&
  end.second() === 0 &&
  end.millisecond() === 0 &&
  end.isAfter(start);

/**
 * Galactus all-day `endDate` is often exclusive next-midnight. The holiday
 * list and edit form use inclusive calendar days, so convert before display
 * or save-back. Otherwise each edit adds another day.
 */
export function toInclusiveHolidayPeriod(
  period: UnavailabilityPeriod,
  timeZone: string
): UnavailabilityPeriod {
  const range = resolveHolidayInstantRange(period, timeZone);
  if (!range) return period;

  const startLocal = range.start.tz(timeZone);
  const endLocal = range.end.tz(timeZone);
  const startDay = startLocal.format("YYYY-MM-DD");
  const exclusiveMidnight = isExclusiveMidnight(endLocal, startLocal);
  const hasExplicitClocks = Boolean(period.startTime && period.endTime);
  const treatAsAllDay = isAllDayHoliday(period) || (exclusiveMidnight && !hasExplicitClocks);
  const endDay =
    treatAsAllDay && exclusiveMidnight
      ? endLocal.subtract(1, "day").format("YYYY-MM-DD")
      : endLocal.format("YYYY-MM-DD");

  if (treatAsAllDay) {
    return {
      ...period,
      startDate: `${startDay}T00:00:00`,
      endDate: `${endDay}T00:00:00`,
      startTime: undefined,
      endTime: undefined,
    };
  }

  return {
    ...period,
    startDate: `${startDay}T${startLocal.format("HH:mm")}:00`,
    endDate: `${endDay}T${endLocal.format("HH:mm")}:00`,
  };
}

/** Format YYYY-MM-DD (from an ISO string) as a local calendar date, not UTC. */
export function formatHolidayCalendarDate(
  iso: string,
  locale: Intl.LocalesArgument = "en-US"
): string {
  const day = calendarDay(iso);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return iso;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(locale, {
    weekday: "short",
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
}
